import { useCallback } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Rule, Schedule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AvailabilityExceptionInfo, EventData, WorkingHourInfo } from '../../components/calendar-view/types';
import { getDateRangeFromRRule, getScheduleInfoFromRRule } from '../../rrule-helper';
import { useCalendarV3ShallowStore } from '../../stores/use-calendar-v3-store';
import { CalendarEventsV3DateFormat } from '../../utils';
import { formatDateTime, getCalendarStartAndEndDateTime, getDateString, getTimeString } from '../../utils/date-helpers';

type GetCalendarEventByPractitionerIdParamsType = {
  calendarEvents: CalendarEvent[];
  practitionerId: string;
  locationId: string;
};

type GetEntityOutOfOfficeEventsArgs = {
  schedules: Schedule[];
  entityId: string;
  locationId: string;
};

const getExceptionRelatedInfoFromRRule = (
  rrule: string,
  duration: number,
  calendarStartDateTime: string,
  calendarEndDateTime: string
) => {
  const { startDateTime, endDateTime, tzid } = getDateRangeFromRRule(rrule);

  const startUTCDateTime = formatDateTime(startDateTime, {
    fromTimezone: tzid ?? '',
    toTimezone: 'UTC',
    outputFormat: CalendarEventsV3DateFormat,
  });
  const endUTCDateTime = formatDateTime(dayjs.utc(endDateTime).add(duration, 'minute'), {
    fromTimezone: tzid ?? '',
    toTimezone: 'UTC',
    outputFormat: CalendarEventsV3DateFormat,
  });

  const shouldShowEvent =
    dayjs(calendarStartDateTime).isBetween(startUTCDateTime, endUTCDateTime, null, '[)') ||
    dayjs(calendarEndDateTime).isBetween(startUTCDateTime, endUTCDateTime, null, '[]') ||
    dayjs(startUTCDateTime).isBetween(calendarStartDateTime, calendarEndDateTime, null, '[]') ||
    dayjs(endUTCDateTime).isBetween(calendarStartDateTime, calendarEndDateTime, null, '(]');

  return { startDateTime, startUTCDateTime, endDateTime, endUTCDateTime, shouldShowEvent };
};

export const useGetCalendarEventsV3 = () => {
  const { calendarDate } = useCalendarV3ShallowStore('calendarDate');
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');

  const getMappedCalendarEventData = (calendarEvent: CalendarEvent): EventData => {
    const hasName = calendarEvent.attendeeDetails?.firstName || calendarEvent.attendeeDetails?.lastName;
    const personName = `${calendarEvent.attendeeDetails?.firstName} ${calendarEvent?.attendeeDetails?.lastName}`.trim();
    const name = hasName ? personName : 'Unknown';

    const startDate = dayjs(`${calendarEvent.startDate} ${calendarEvent.startTime}`);
    const endDate = dayjs(`${calendarEvent.endDate} ${calendarEvent.endTime}`);

    return {
      id: calendarEvent.id ?? '',
      isNonIntegratedSource: !calendarEvent.isIntegrated,
      startHour: getTimeString(startDate),
      endHour: getTimeString(endDate),
      type: 'second',
      status: calendarEvent.attendeeStatus,
      appointmentType: calendarEvent.eventType,
      appointmentTypeId: calendarEvent.referenceTypeId ?? '',
      eventId: calendarEvent.id ?? '',
      createdBySourceId: calendarEvent.sourceTenantId ?? '',
      workstationIds: calendarEvent.operatoryId ? [calendarEvent.operatoryId] : [],
      name: name,
      patientId: calendarEvent.attendeeId ?? '',
      locationId: calendarEvent.locationId ?? '',
      startDate: getDateString(startDate),
      endDate: getDateString(endDate),
      referenceId: calendarEvent.referenceId ?? '',
      personStatus: calendarEvent.attendeeDetails?.status ?? '',
    };
  };

  const getPractitionerCalendarEvents = useCallback(
    ({ calendarEvents, locationId, practitionerId }: GetCalendarEventByPractitionerIdParamsType): EventData[] => {
      return (calendarEvents ?? [])
        .filter(
          (event) => !!event.organizerId && event.locationId === locationId && event.organizerId === practitionerId
        )
        .map((calendarEvent) => getMappedCalendarEventData(calendarEvent));
    },
    []
  );

  const getEntityAvailabilityExceptions = useCallback(
    ({ schedules, locationId, entityId }: GetEntityOutOfOfficeEventsArgs): AvailabilityExceptionInfo[] => {
      const locationTimezone = locationTimezoneMap[locationId]?.timezone;
      const { startDateTime: calendarStartDateTime, endDateTime: calendarEndDateTime } = getCalendarStartAndEndDateTime(
        calendarDate,
        locationTimezone
      );

      if (!calendarStartDateTime || !calendarEndDateTime) {
        return [];
      }

      const entitySchedule = schedules.find(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      const availabilityExceptions = entitySchedule?.recurrenceRules?.override?.availabilities || [];

      return availabilityExceptions
        .map<AvailabilityExceptionInfo | null>(({ rrule = '', duration = 0 }) => {
          const info = getExceptionRelatedInfoFromRRule(rrule, duration, calendarStartDateTime, calendarEndDateTime);
          if (!rrule || !duration || !info.shouldShowEvent) return null;

          return {
            startTime: formatDateTime(info.startUTCDateTime, {
              fromTimezone: 'UTC',
              toTimezone: locationTimezone,
              outputFormat: 'hh:mm A',
            }),
            duration,
          };
        })
        .filter((item) => item !== null);
    },
    [calendarDate, locationTimezoneMap]
  );

  const getEntityWorkingHours = useCallback(
    ({ schedules, locationId, entityId }: GetEntityOutOfOfficeEventsArgs): WorkingHourInfo[] => {
      const entitySchedule = schedules.find(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      const scheduleWorkingHours = entitySchedule?.recurrenceRules?.workingHours || [];
      const scheduleBreakHours = entitySchedule?.recurrenceRules?.breaks || [];

      const workingHours = scheduleWorkingHours.reduce((acc: WorkingHourInfo[], { rrule, duration = 0 }: Rule) => {
        if (!rrule) return acc;

        const { startTime, daysOfWeek } = getScheduleInfoFromRRule(rrule, duration, 'hh:mm A');

        if (daysOfWeek.includes(dayjs(calendarDate).day())) {
          acc.push({ startTime, duration, breaks: [] });
        }
        return acc;
      }, []);

      const breakHours = scheduleBreakHours.reduce(
        (acc: Pick<WorkingHourInfo, 'startTime' | 'duration'>[], { rrule, duration = 0 }: Rule) => {
          if (!rrule) return acc;

          const { startTime, daysOfWeek } = getScheduleInfoFromRRule(rrule, duration, 'hh:mm A');

          if (daysOfWeek.includes(dayjs(calendarDate).day())) {
            acc.push({ startTime, duration });
          }
          return acc;
        },
        []
      );

      return workingHours.map((workingHour) => ({ ...workingHour, breaks: breakHours }));
    },
    [calendarDate]
  );

  const getEntityExceptionEvents = useCallback(
    ({ schedules, locationId, entityId }: GetEntityOutOfOfficeEventsArgs): EventData[] => {
      const { startDateTime: calendarStartDateTime, endDateTime: calendarEndDateTime } = getCalendarStartAndEndDateTime(
        calendarDate,
        locationTimezoneMap[locationId]?.timezone
      );

      if (!calendarStartDateTime || !calendarEndDateTime) {
        return [];
      }

      const entitySchedule = schedules.filter(
        (schedule) => schedule.locationId === locationId && schedule.id === entityId
      );

      const exceptions = entitySchedule[0]?.recurrenceRules?.override?.unAvailabilities || [];

      const result = exceptions.map<EventData>(({ id = '', name = '', rrule, duration = 0 }: Rule): any => {
        if (rrule) {
          const info = getExceptionRelatedInfoFromRRule(rrule, duration, calendarStartDateTime, calendarEndDateTime);

          if (info.shouldShowEvent) {
            // NOTE: The event start and end dates could be different than the calendar date in case of multi-day recurring events.
            // But we need to show the event on the calendar for selected calendar date. So, using the calendar date for start and end dates.
            const startDate = dayjs(calendarDate).format('MM/DD/YYYY');
            const startHour = dayjs.utc(info.startDateTime).format('hh:mm A');
            const endDateTime = dayjs(`${calendarDate} ${startHour}`).add(duration, 'minute');
            const endDate = endDateTime.format('MM/DD/YYYY');
            const endHour = endDateTime.format('hh:mm A');

            return {
              id,
              name,
              type: 'unavailable',
              startDate,
              startHour,
              endDate,
              endHour,
              eventId: id,
              locationId: locationId,
            };
          }

          return null;
        }

        return null;
      });

      return result.filter((item) => item !== null);
    },
    [calendarDate, locationTimezoneMap]
  );

  return {
    getPractitionerCalendarEvents,
    getEntityWorkingHours,
    getEntityExceptionEvents,
    getEntityAvailabilityExceptions,
  };
};
